import { z } from "zod";

import { INITIAL_ADDRESS_LABEL } from "./address-copy";
import { ADDRESS_USE_VALUES } from "../platform/domain-values";
import type { Address, Contact } from "../platform/schema";

export const ADDRESS_LIMITS = { label: 200, lines: 4000, country: 200 } as const;

export const ADDRESS_ROLES = ["defaultBilling", "defaultShipping"] as const;
export type AddressRole = (typeof ADDRESS_ROLES)[number];

export type AddressUse = (typeof ADDRESS_USE_VALUES)[number];
export const ADDRESS_USE_ROLES: Record<AddressUse, AddressRole> = { billing: "defaultBilling", shipping: "defaultShipping" };
export type PrintedAddresses = Record<AddressUse, string | null>;

export const addressInputSchema = z.object({
  label: z.string().trim().min(1).max(ADDRESS_LIMITS.label),
  lines: z.string().trim().min(1).max(ADDRESS_LIMITS.lines),
  country: z.string().trim().max(ADDRESS_LIMITS.country),
  defaultBilling: z.boolean(),
  defaultShipping: z.boolean(),
});

export type AddressInput = z.infer<typeof addressInputSchema>;

export type AddressOwner = { tenantId: number; contactId: number | null };

export function formatAddress(address: { lines: string; country: string }): string {
  return [address.lines, address.country].filter(Boolean).join("\n");
}

export function belongsTo(address: Pick<Address, "tenantId" | "contactId">, owner: AddressOwner): boolean {
  return address.tenantId === owner.tenantId && address.contactId === owner.contactId;
}

export function isEligibleAddress(
  address: Pick<Address, "id" | "tenantId" | "contactId" | "archivedAt"> | undefined,
  owner: AddressOwner,
  keptId: number | null = null,
): boolean {
  if (!address || !belongsTo(address, owner)) return false;
  return address.archivedAt == null || address.id === keptId;
}

export function activeAddresses<T extends Pick<Address, "archivedAt">>(rows: readonly T[]): T[] {
  return rows.filter((row) => row.archivedAt == null);
}

export function defaultAddress<T extends Pick<Address, "archivedAt" | AddressRole>>(rows: readonly T[], role: AddressRole): T | undefined {
  const active = activeAddresses(rows);
  return active.find((row) => row[role]) ?? active[0];
}

export function initialContactAddress(contact: Pick<Contact, "address" | "country">): AddressInput | null {
  const lines = contact.address?.trim() ?? "";
  if (!lines) return null;
  return { label: INITIAL_ADDRESS_LABEL, lines, country: contact.country?.trim() ?? "", defaultBilling: true, defaultShipping: true };
}

export function printedAddress(rows: readonly Address[], use: AddressUse, chosenId: number | null, legacy: string | null = null): string | null {
  const chosen = chosenId == null ? undefined : rows.find((row) => row.id === chosenId);
  const row = chosen ?? defaultAddress(rows, ADDRESS_USE_ROLES[use]);
  return row ? formatAddress(row) : legacy || null;
}

export function withFirstAddressDefaults(input: AddressInput, activeCount: number): AddressInput {
  return activeCount > 0 ? input : { ...input, defaultBilling: true, defaultShipping: true };
}
